import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useVenueStore } from '../store/useVenueStore';
import { useTranslation } from 'react-i18next';
import { X, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { Venue } from '../types/venue';

declare global {
  interface Window {
    mapboxgl: any;
  }
}

export const VenueMap: React.FC = () => {
  const { filteredVenues } = useVenueStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [currentZoom, setCurrentZoom] = useState<number>(5);
  const [currentCenter, setCurrentCenter] = useState<[number, number]>([11.97, 57.70]);
  const mapInitialized = useRef(false);
  const initialLoad = useRef(true);
  const lastSelectedVenueRef = useRef<string | null>(null);
  const isFromModalClose = useRef(false);

  // Load mapbox resources
  useEffect(() => {
    if (mapInitialized.current) return;

    const loadMapbox = async () => {
      try {
        // Add mapbox CSS
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.css';
        document.head.appendChild(link);

        // Load script
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.js';
          script.async = true;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('Failed to load Mapbox script'));
          document.body.appendChild(script);
        });

        // Initialize map now that script is loaded
        initializeMap();
        mapInitialized.current = true;
      } catch (error) {
        console.error('Error loading Mapbox:', error);
        setMapError("Failed to load map resources");
      }
    };

    loadMapbox();

    // Cleanup function
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      mapInitialized.current = false;
    };
  }, []);

  // Initialize map
  const initializeMap = useCallback(() => {
    if (!mapRef.current || !window.mapboxgl || mapInstanceRef.current) return;
    
    try {
      window.mapboxgl.accessToken = 'pk.eyJ1IjoiZnJvc3RndWFyZHMiLCJhIjoiY204b2dqdHJmMDAzczJtc2E5YjE0YTdwcyJ9.UpsK4OO7Zt35vHylYF_gog';
      
      const map = new window.mapboxgl.Map({
        container: mapRef.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center: currentCenter,
        zoom: currentZoom
      });
      
      mapInstanceRef.current = map;

      // Track map state
      map.on('zoomend', () => {
        setCurrentZoom(map.getZoom());
      });
      
      map.on('moveend', () => {
        setCurrentCenter([map.getCenter().lng, map.getCenter().lat]);
      });

      map.on('load', () => {
        setMapLoaded(true);
        updateMarkers();
        
        // Only fit bounds on initial load
        if (initialLoad.current && filteredVenues.length > 0) {
          fitBoundsToVenues();
          initialLoad.current = false;
        }
      });
      
      map.addControl(new window.mapboxgl.NavigationControl());
    } catch (error) {
      console.error('Error initializing map:', error);
      setMapError("Failed to initialize map");
      setMapLoaded(false);
    }
  }, [currentCenter, currentZoom]);

  // Fit map bounds to show all venues
  const fitBoundsToVenues = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map || filteredVenues.length === 0) return;
    
    const bounds = new window.mapboxgl.LngLatBounds();
    
    filteredVenues.forEach(venue => {
      bounds.extend([venue.coordinates.lng, venue.coordinates.lat]);
    });
    
    map.fitBounds(bounds, {
      padding: 50,
      maxZoom: 15,
      duration: 1000
    });
  }, [filteredVenues]);
  
  // Create a helper function to get reliable venue images
  const getVenueImageUrl = (venue: Venue) => {
    // If venue has a valid image that's not from Unsplash, use it
    if (venue.image && !venue.image.includes('unsplash.com')) {
      return venue.image;
    }
    
    // For specific venues with known good images
    const knownImages: Record<string, string> = {
      // Already added venues
      'hotel-bellora': 'https://homeofess.com/pub_images/original/bellora-outside.jpg',
      'ullevi-konferens': '/assets/images/ullevi.jpg',
      'kviberg-hotel': '/assets/images/kviberg-hotel.jpg',
      'arken-hotel': 'https://asset.cloudinary.com/hg5jrqj0n/a5d51a1c3bc0ea6e83cd62ff1d3a26e9/arken.jpg',
      'hjortviken-hotel': '/assets/images/hjortviken.jpg',
      'svenska-massan': '/assets/images/svenska-massan.webp',
      'elite-park-avenue': 'https://elite.se/globalassets/elite-hotels/hotell/goteborg/elite-park-avenue-hotel/hotellbilder/elite-park-avenue-fasad-natt-1920x1080.jpg',
      'villa-johanneberg': 'https://www.essgroup.se/wp-content/uploads/2018/09/villa-johanneberg-large-top-slide-6-1024x630.jpg',
      
      // Additional venues
      'hotel-pigalle': '/assets/images/hotel-pigalle-room.jpg',
      'stationshuset': 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRpXxRnA-f1jvGMGIfDVEXqyDq5n2PVAXLAyQ&usqp=CAU',
      'jacy-hotel': 'https://dynamic-media-cdn.tripadvisor.com/media/photo-o/1c/d2/cf/8f/caption.jpg?w=1200&h=-1&s=1',
      'clarion-hotel-post': 'https://media.licdn.com/dms/image/C4E1BAQHkT-htyErUPg/company-background_10000/0/1519799792722?e=1706245200&v=beta&t=7lNQnlj7k4XKXYeRVb4fJNTR9rCnNweP4NblHl_8cCc',
      'landvetter-airport-hotel': 'https://image.bokadirekt.se/images/24498/-Landvetter-Airport-Hotel.jpg',
      'quality-hotel-panorama': 'https://www.nordicchoicehotels.com/globalassets/global/hotel-pictures/quality-hotel/quality-hotel-panorama/general/quality_hotel_panorama_exterior_02.jpg',
      'comfort-hotel-goteborg': 'https://cdn.wikimg.net/en/strategywiki/images/7/7c/Comfort_Hotel_G%C3%B6teborg.jpg',
      'apple-hotel': 'https://apple-hotel.com/wp-content/uploads/2023/07/Header1-1.jpg',
      'hotel-royal': 'https://www.elite.se/globalassets/elite-hotels/hotell/goteborg/hotel-royal/hotellbilder/elite-hotel-royal-fasad-1920x1080.jpg',
      'scandic-opalen': 'https://www.scandichotels.com/imageVault/publishedmedia/fvjtv7pb7fbmlkbfqct6/Scandic-Opalen-exterior-view.jpg',
      'scandic-rubinen': 'https://www.scandichotels.com/imageVault/publishedmedia/5s1ikehdngb06aeqx5a8/Scandic-Rubinen-exterior-facade.jpg',
      'hotel-riverton': 'https://images.riverton.se/Hotel-Riverton-fasad-fors.jpg',
      'liseberg-hotel': 'https://www.liseberg.com/Lmnab/media/hk4/hkab/externalassets/Images/LisebergHotell/Lisebergs-Hotell.png',
      'copenhagen-conference': 'https://asset.hotelwiz.net/m/r960x540/asset-files/property/12084/exterior3-12084-20182210065-o.jpg',
      'scandic-crown': 'https://www.scandichotels.com/imageVault/publishedmedia/j6k0ldbxfbmm1yh4sglz/Scandic-Crown-exterior-view.jpg'
    };
    
    // Check if we have a known good image for this venue
    if (venue.id && knownImages[venue.id]) {
      return knownImages[venue.id];
    }
    
    // Fallback: Try to guess based on venue name
    const nameKey = venue.name.toLowerCase().replace(/[^a-z0-9]/g, '-');
    if (knownImages[nameKey]) {
      return knownImages[nameKey];
    }
    
    // Try variations of the venue ID
    const variations = [
      venue.id,
      venue.id.replace('-hotel', ''),
      venue.id.replace('-conference', ''),
      venue.id + '-hotel',
      'hotel-' + venue.id
    ];
    
    for (const variation of variations) {
      if (knownImages[variation]) {
        return knownImages[variation];
      }
    }
    
    // Final fallback to a placeholder with venue initial
    return `https://via.placeholder.com/70x70/2D2F4E/FFFFFF?text=${encodeURIComponent(venue.name.charAt(0))}`;
  };

  // Update markers
  const updateMarkers = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map || !window.mapboxgl) return;
    
    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
    
    // Add new markers for filtered venues
    const newMarkers = filteredVenues.map(venue => {
      // Create marker elements
      const markerContainer = document.createElement('div');
      markerContainer.className = 'venue-marker-container';
      markerContainer.style.cursor = 'pointer';
      markerContainer.style.zIndex = '5';
      
      const pinElement = document.createElement('div');
      pinElement.className = 'venue-pin';
      
      // Style the pin
      pinElement.style.width = '30px';
      pinElement.style.height = '30px';
      pinElement.style.backgroundColor = venue.recommended ? '#fabddf' : '#2D2F4E';
      pinElement.style.borderRadius = '50%';
      pinElement.style.display = 'flex';
      pinElement.style.alignItems = 'center';
      pinElement.style.justifyContent = 'center';
      pinElement.style.color = venue.recommended ? '#2D2F4E' : 'white';
      pinElement.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
      pinElement.style.border = venue.recommended ? '2px solid #fabddf' : 'none';
      pinElement.style.transition = 'all 0.25s cubic-bezier(0.25, 1, 0.5, 1)';
      pinElement.style.position = 'relative';
      
      // Add pin icon
      pinElement.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"></path>
          <circle cx="12" cy="10" r="3"></circle>
        </svg>
      `;
      
      // Create tooltip element for hover preview
      const tooltipElement = document.createElement('div');
      tooltipElement.className = 'venue-tooltip';
      tooltipElement.style.position = 'absolute';
      tooltipElement.style.bottom = '100%';
      tooltipElement.style.left = '50%';
      tooltipElement.style.transform = 'translateX(-50%)';
      tooltipElement.style.backgroundColor = 'white';
      tooltipElement.style.color = '#333';
      tooltipElement.style.padding = '10px 12px';
      tooltipElement.style.borderRadius = '8px';
      tooltipElement.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
      tooltipElement.style.zIndex = '20';
      tooltipElement.style.width = 'auto';
      tooltipElement.style.minWidth = '180px';
      tooltipElement.style.maxWidth = '240px';
      tooltipElement.style.marginBottom = '12px';
      tooltipElement.style.opacity = '0';
      tooltipElement.style.transition = 'opacity 0.2s ease-in-out, transform 0.2s ease-in-out';
      tooltipElement.style.pointerEvents = 'none';
      tooltipElement.style.border = '1px solid rgba(0,0,0,0.1)';
      tooltipElement.style.transform = 'translateX(-50%) translateY(8px)';
      
      // Create content for tooltip without image
      tooltipElement.innerHTML = `
        <div>
          <div style="font-weight: 600; font-size: 14px; margin-bottom: 5px; color: #2D2F4E; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
            ${venue.name}
          </div>
          <div style="display: flex; align-items: center; font-size: 12px; color: #666; margin-bottom: 4px; white-space: nowrap;">
            <div style="display: flex; margin-right: 10px;">
              ${Array.from({ length: 5 }).map((_, i) => `
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="${i < Math.floor(venue.rating) ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-star" style="color: ${i < Math.floor(venue.rating) ? '#FACC15' : '#D1D5DB'}; margin-right: 1px;">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                </svg>
              `).join('')}
              <span style="margin-left: 3px; font-size: 11px;">(${venue.reviews.length})</span>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 4px; flex-shrink: 0;">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <span>${venue.capacity.min}-${venue.capacity.max} ${t('venues.capacity') || 'people'}</span>
          </div>
          ${venue.recommended ? 
            `<div style="display: inline-block; background-color: #fabddf; color: #2D2F4E; font-size: 10px; font-weight: 600; padding: 2px 6px; border-radius: 12px; margin-top: 2px; white-space: nowrap;">
              ✨ ${t('venues.recommended') || 'Recommended'}
            </div>` : 
            ''
          }
      </div>
      `;
      
      // Add a subtle arrow at the bottom
      const arrowElement = document.createElement('div');
      arrowElement.style.position = 'absolute';
      arrowElement.style.bottom = '-5px';
      arrowElement.style.left = '50%';
      arrowElement.style.marginLeft = '-5px';
      arrowElement.style.width = '10px';
      arrowElement.style.height = '10px';
      arrowElement.style.backgroundColor = 'white';
      arrowElement.style.transform = 'rotate(45deg)';
      arrowElement.style.borderRight = '1px solid rgba(0,0,0,0.1)';
      arrowElement.style.borderBottom = '1px solid rgba(0,0,0,0.1)';
      tooltipElement.appendChild(arrowElement);
      
      markerContainer.appendChild(pinElement);
      markerContainer.appendChild(tooltipElement);
      
      // Enhanced hover effect
      markerContainer.addEventListener('mouseenter', () => {
        markerContainer.style.zIndex = '10';
        pinElement.style.transform = 'scale(1.25) translateY(-5px)';
        pinElement.style.boxShadow = '0 6px 12px rgba(0,0,0,0.15)';
        
        // Show tooltip with animation
        tooltipElement.style.opacity = '1';
        tooltipElement.style.transform = 'translateX(-50%) translateY(0)';
        
        if (!venue.recommended) {
          pinElement.style.backgroundColor = '#3d3f5f';
        }
      });
      
      markerContainer.addEventListener('mouseleave', () => {
        markerContainer.style.zIndex = '5';
        pinElement.style.transform = '';
        pinElement.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
        
        // Hide tooltip with animation
        tooltipElement.style.opacity = '0';
        tooltipElement.style.transform = 'translateX(-50%) translateY(8px)';
        
        if (!venue.recommended) {
          pinElement.style.backgroundColor = '#2D2F4E';
        }
      });
      
      // Add click handler
      markerContainer.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        navigate(`/venues/${venue.id}`);
      });
      
      // Create marker
      const marker = new window.mapboxgl.Marker({
        element: markerContainer,
        anchor: 'bottom',
        clickTolerance: 0
      })
        .setLngLat([venue.coordinates.lng, venue.coordinates.lat])
        .addTo(map);
        
      return marker;
    });
    
    markersRef.current = newMarkers;
  }, [filteredVenues, navigate, t]);
  
  // Update markers when filtered venues change
  useEffect(() => {
    if (mapLoaded && mapInstanceRef.current) {
      updateMarkers();
      
      // Check if this was triggered by closing a modal
      const wasModalJustClosed = lastSelectedVenueRef.current !== null;
      
      // Only fit bounds if venue list changed and we're not just closing a modal
      if (filteredVenues.length > 0 && !wasModalJustClosed) {
        fitBoundsToVenues();
      }
      
      // Update the ref for next check
      lastSelectedVenueRef.current = filteredVenues.length > 0 ? filteredVenues[0].id || null : null;
    }
  }, [filteredVenues, mapLoaded, updateMarkers, fitBoundsToVenues]);

  if (mapError) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md text-center">
          <p className="text-lg font-semibold mb-2">{t('venues.mapError')}</p>
          <p className="mb-4">{mapError}</p>
          <p className="text-sm text-gray-600">
            {t('venues.viewInList')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 relative">
      {/* Header */}
      <div className="bg-white p-3 shadow-sm z-10 border-b flex justify-between items-center">
        <div>
          <h3 className="text-sm font-medium text-gray-700">
            {t('venues.mapView')}
                        </h3>
          <div className="text-xs text-gray-500 mt-0.5">
            {filteredVenues.length} {filteredVenues.length === 1 ? t('venues.venue') : t('venues.venues')} {t('venues.matchFilters')}
                        </div>
                      </div>
                    </div>

      {/* Filter indicator */}
      {filteredVenues.length < 15 && filteredVenues.length > 0 && (
        <div className="absolute top-16 left-3 z-20 bg-white px-3 py-1 rounded shadow-sm text-xs text-gray-700 border border-gray-200">
          {t('venues.filtered')}
        </div>
      )}

      {/* Map container */}
      <div ref={mapRef} className="flex-1 relative">
        {!mapLoaded && !mapError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 bg-opacity-80 z-10">
            <div className="text-center p-4">
              <div className="w-8 h-8 border-4 border-[#2D2F4E] border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p>{t('venues.loadingMap')}</p>
                  </div>
                </div>
        )}
      </div>
    </div>
  );
};